import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AssessmentRunOption } from '../../shared/utils';
import March20kmInnerForm from '../March20KmInnerForm';
import {
    baseMarch20KmPerformanceFormSchema,
    type EditMarch20KmPerformanceFormData,
} from '../March20KmPerformanceFormConfig';
import type { March20kmPerformanceRowData } from '../March20kmPerformanceTab';
import {
    UpdatePerformanceMarch20KmResultDocument,
    type UpdatePerformanceMarch20KmResultMutation,
} from './UpdatePerformanceMarch20KmResults.api';
import client from '@/apolloClient';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { useGetCompanyId } from '@/contexts/UserSession';
import { mapPassFailWithNullToResult, mapResultToPassFailWithNull, splitSecondsToHoursMinutes } from '@/utils/common';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

interface EditPerformanceMarch20KmModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    rowData: March20kmPerformanceRowData | null;
    assessmentRunOptions: AssessmentRunOption[];
}

const EditPerformanceMarch20KmModal = ({
    open,
    onClose,
    rowData,
    assessmentRunOptions,
}: EditPerformanceMarch20KmModalProps) => {
    const { t } = useTranslation('performanceResult');
    const selectedCompanyId = useGetCompanyId();

    const { hours: timingSecHours, minutes: timingSecMinutes } = splitSecondsToHoursMinutes(rowData?.timingSec);

    const form = useForm<EditMarch20KmPerformanceFormData>({
        mode: 'onChange',
        resolver: zodResolver(baseMarch20KmPerformanceFormSchema),
        defaultValues: {
            timingSec: rowData?.timingSec ?? undefined,
            result: mapResultToPassFailWithNull(rowData?.result),
            date: rowData?.assessmentDate ?? undefined,
            timingSecMinutes,
            timingSecHours,
            isTiedToAssessmentRun: rowData?.isTiedToAssessmentRun ?? false,
            assessmentRunId: rowData?.assessmentRunId ?? undefined,
        },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    const handleSubmit = async () => {
        if (!(await form.trigger()) || !rowData) {
            return;
        }

        const values = form.getValues();

        try {
            const { data } = await client.mutate<UpdatePerformanceMarch20KmResultMutation>({
                mutation: UpdatePerformanceMarch20KmResultDocument,
                variables: {
                    id: rowData.id,
                    input: {
                        companyId: selectedCompanyId || '',
                        assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId || undefined : undefined,
                        result: {
                            timingSec: values.timingSec ?? undefined,
                            result: mapPassFailWithNullToResult(values.result),
                        },
                        completedAt: values.date ?? undefined,
                    },
                },
            });

            if (data?.updateMarch20KmResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.updateMarch20KmResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage =
                    specificErrorField?.message || t('updateError.description', { type: '20km Combat March' });

                showErrorNotification({
                    message: t('updateError.title', { type: '20km Combat March' }),
                    description: errorMessage,
                });

                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('updateSuccess.title', { type: '20km Combat March' }),
                    description: t('updateSuccess.message', { type: '20km Combat March' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error updating 20km Combat March performance result:', error);
            throw error;
        }
    };

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button outline onClick={() => onClose()}>
                    {t('march20km.editModal.cancelBtn')}
                </Button>
                <Button color="teal" onClick={handleSubmit}>
                    {t('march20km.editModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('march20km.editModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('march20km.form.assessmentDate')} required />
                        </div>
                        <March20kmInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default EditPerformanceMarch20KmModal;
